// Frameworks, requirements and requirement mappings (APP-004 R3 P1, UI-P3).
//
// Types are transcribed from the frozen Policy AI contract
// (v0.3.0-governance-compliance) and used as-is: coverage, gaps, comparisons
// and mapping state all arrive computed, and the browser renders them
// unchanged. Nothing here derives a status, a score or a coverage figure.
//
// Transport is the shared GovernanceHttp, so every call goes through the
// Studio Worker proxy, which remains the authorization boundary.
import { GovernanceHttp, queryString } from "./governanceHttp";
import type { GovernanceClientOptions } from "./governanceHttp";

export type FrameworkScope = "global" | "tenant";
export type FrameworkContentPolicy = "identifier_only" | "summary_permitted" | "licensed_only";
export type FrameworkVersionStatus = "draft" | "active" | "superseded" | "retired";
export type RequirementKind = "family" | "control" | "requirement" | "criterion";
export type MappingState = "proposed" | "verified" | "rejected" | "superseded" | "stale";
export type MappingStrength = "full" | "partial" | "supporting";
export type MappingMethod = "manual" | "ai_suggested" | "imported" | "derived";
export type MappingDecision = "verified" | "rejected" | "reopened" | "marked_stale" | "superseded";
export type CoverageStatus = "covered" | "weak" | "stale" | "uncovered";
export type GapStatus = "uncovered" | "weak" | "stale";
export type RequirementContentKind = "licensed_text" | "odingard_summary" | "customer_note";

export interface ComplianceFramework {
  id: string;
  organizationId: string | null;
  scope: FrameworkScope;
  frameworkKey: string;
  name: string;
  authority: string;
  description: string;
  contentPolicy: FrameworkContentPolicy;
  copiedFromFrameworkId: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface FrameworkVersion {
  id: string;
  organizationId: string | null;
  frameworkId: string;
  versionLabel: string;
  status: FrameworkVersionStatus;
  effectiveDate: string | null;
  supersededById: string | null;
  importedFrom: string | null;
  requirementCount: number;
  contentHash: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  activatedAt: string | null;
  supersededAt: string | null;
}

export interface FrameworkRequirement {
  id: string;
  organizationId: string | null;
  frameworkVersionId: string;
  parentId: string | null;
  requirementKey: string;
  title: string;
  summary: string;
  kind: RequirementKind;
  sortOrder: number;
  applicability: Record<string, unknown>;
  isTestable: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface FrameworkLicense {
  id: string;
  organizationId: string;
  frameworkVersionId: string;
  licenseType: string;
  licensor: string;
  reference: string;
  permitsFullText: boolean;
  grantedBy: string;
  grantedAt: string;
  expiresAt: string | null;
  notes: string;
  revokedAt: string | null;
  createdAt: string;
}

export interface RequirementContent {
  id: string;
  organizationId: string | null;
  requirementId: string;
  licenseId: string | null;
  contentKind: RequirementContentKind;
  body: string;
  sourceDocumentId: string | null;
  contentHash: string;
  createdBy: string;
  createdAt: string;
}

/** A row the backend refused to return, with the reason it withheld it. */
export interface WithheldContent {
  id: string;
  reason: string;
}

export interface MappingCitation {
  sourceType: string;
  sourceId: string;
  excerpt: string;
}

export interface RequirementMapping {
  id: string;
  organizationId: string;
  requirementId: string;
  frameworkVersionId: string;
  subjectType: "policy" | "policy_version" | "version_section" | "claim";
  subjectId: string;
  policyId: string | null;
  subjectVersionId: string | null;
  state: MappingState;
  strength: MappingStrength;
  method: MappingMethod;
  rationale: string;
  citations: MappingCitation[];
  confidence: number | null;
  model: string | null;
  analyzerVersion: string | null;
  promptVersion: string | null;
  supersededById: string | null;
  staleReason: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface MappingVerification {
  id: string;
  organizationId: string;
  mappingId: string;
  actorId: string;
  decision: MappingDecision;
  rationale: string;
  fromState: MappingState;
  toState: MappingState;
  decidedAt: string;
}

export interface RequirementCoverage {
  requirementId: string;
  requirementKey: string;
  title: string;
  kind: RequirementKind;
  verifiedCount: number;
  proposedCount: number;
  staleCount: number;
  strongestStrength: MappingStrength | null;
  status: CoverageStatus;
}

export interface CoverageSummary {
  frameworkVersionId: string;
  totalRequirements: number;
  covered: number;
  weak: number;
  stale: number;
  uncovered: number;
  requirements: RequirementCoverage[];
}

export interface RequirementDelta {
  requirementKey: string;
  change: "added" | "removed" | "changed" | "unchanged";
  fromTitle: string | null;
  toTitle: string | null;
  carriedMappingCount: number;
}

export interface FrameworkVersionComparison {
  fromVersionId: string;
  toVersionId: string;
  added: number;
  removed: number;
  changed: number;
  unchanged: number;
  deltas: RequirementDelta[];
}

export interface ImportRequirementRow {
  requirementKey: string;
  title: string;
  summary?: string;
  kind?: RequirementKind;
  parentKey?: string | null;
  sortOrder?: number;
  isTestable?: boolean;
  /** Protected text. Accepted only with a live licence that permits full text. */
  licensedText?: string;
}

export interface ImportRequirementsResult {
  version: FrameworkVersion;
  imported: number;
  licensedTextAccepted: number;
}

export interface CreateFrameworkInput {
  frameworkKey: string;
  name: string;
  authority?: string;
  description?: string;
  contentPolicy?: FrameworkContentPolicy;
}

export interface CreateFrameworkLicenseInput {
  licenseType: string;
  licensor: string;
  reference?: string;
  permitsFullText?: boolean;
  grantedAt: string;
  expiresAt?: string | null;
  notes?: string;
}

export interface CreateMappingInput {
  requirementId: string;
  subjectType: RequirementMapping["subjectType"];
  subjectId: string;
  policyId?: string | null;
  subjectVersionId?: string | null;
  strength?: MappingStrength;
  rationale?: string;
  citations?: MappingCitation[];
}

export interface MappingFilter {
  frameworkVersionId?: string;
  requirementId?: string;
  policyId?: string;
  state?: MappingState;
  method?: MappingMethod;
}

export class FrameworksClient {
  private http: GovernanceHttp;

  constructor(opts: GovernanceClientOptions | GovernanceHttp) {
    this.http = opts instanceof GovernanceHttp ? opts : new GovernanceHttp(opts);
  }

  // ----- catalogue and adoption --------------------------------------------
  frameworks(orgId: string, filter: { scope?: FrameworkScope; search?: string } = {}) {
    return this.http.get<{ frameworks: ComplianceFramework[] }>(
      orgId,
      `compliance-frameworks${queryString({ scope: filter.scope, search: filter.search })}`,
    );
  }
  framework(orgId: string, frameworkId: string) {
    return this.http.get<{ framework: ComplianceFramework }>(orgId, `compliance-frameworks/${frameworkId}`);
  }
  registerFramework(orgId: string, input: CreateFrameworkInput) {
    return this.http.post<{ framework: ComplianceFramework }>(orgId, "compliance-frameworks", input);
  }
  adoptFramework(orgId: string, frameworkId: string) {
    return this.http.post<{ framework: ComplianceFramework; version: FrameworkVersion }>(
      orgId,
      `compliance-frameworks/${frameworkId}/adopt`,
    );
  }

  // ----- versions -----------------------------------------------------------
  versions(orgId: string, frameworkId: string) {
    return this.http.get<{ versions: FrameworkVersion[] }>(
      orgId,
      `compliance-frameworks/${frameworkId}/versions`,
    );
  }
  createVersion(orgId: string, frameworkId: string, input: { versionLabel: string; effectiveDate?: string | null }) {
    return this.http.post<{ version: FrameworkVersion }>(
      orgId,
      `compliance-frameworks/${frameworkId}/versions`,
      input,
    );
  }
  activateVersion(orgId: string, versionId: string) {
    return this.http.post<{ version: FrameworkVersion }>(orgId, `framework-versions/${versionId}/activate`);
  }
  compareVersions(orgId: string, versionId: string, toVersionId: string) {
    return this.http.get<{ comparison: FrameworkVersionComparison }>(
      orgId,
      `framework-versions/${versionId}/comparison${queryString({ toVersionId })}`,
    );
  }

  // ----- requirements -------------------------------------------------------
  requirements(orgId: string, versionId: string, search?: string) {
    return this.http.get<{ requirements: FrameworkRequirement[] }>(
      orgId,
      `framework-versions/${versionId}/requirements${queryString({ search })}`,
    );
  }
  importRequirements(
    orgId: string,
    versionId: string,
    input: { requirements: ImportRequirementRow[]; licenseId?: string | null },
  ) {
    return this.http.post<ImportRequirementsResult>(
      orgId,
      `framework-versions/${versionId}/requirements/import`,
      { format: "json", ...input },
    );
  }
  requirementContent(orgId: string, requirementId: string) {
    return this.http.get<{ content: RequirementContent[]; withheld: WithheldContent[] }>(
      orgId,
      `framework-requirements/${requirementId}/content`,
    );
  }
  addRequirementContent(
    orgId: string,
    requirementId: string,
    input: { contentKind: RequirementContentKind; body: string; licenseId?: string | null },
  ) {
    return this.http.post<{ content: RequirementContent }>(
      orgId,
      `framework-requirements/${requirementId}/content`,
      input,
    );
  }

  // ----- licences -----------------------------------------------------------
  licenses(orgId: string, versionId: string) {
    return this.http.get<{ licenses: FrameworkLicense[] }>(orgId, `framework-versions/${versionId}/licenses`);
  }
  createLicense(orgId: string, versionId: string, input: CreateFrameworkLicenseInput) {
    return this.http.post<{ license: FrameworkLicense }>(
      orgId,
      `framework-versions/${versionId}/licenses`,
      input,
    );
  }
  revokeLicense(orgId: string, licenseId: string) {
    return this.http.post<{ license: FrameworkLicense }>(orgId, `framework-licenses/${licenseId}/revoke`);
  }

  // ----- coverage -----------------------------------------------------------
  coverage(orgId: string, versionId: string) {
    return this.http.get<{ coverage: CoverageSummary }>(orgId, `framework-versions/${versionId}/coverage`);
  }
  gaps(orgId: string, versionId: string, status: GapStatus = "uncovered") {
    return this.http.get<{ status: GapStatus; requirements: RequirementCoverage[] }>(
      orgId,
      `framework-versions/${versionId}/gaps${queryString({ status })}`,
    );
  }

  // ----- mappings -----------------------------------------------------------
  mappings(orgId: string, filter: MappingFilter = {}) {
    return this.http.get<{ mappings: RequirementMapping[] }>(
      orgId,
      `requirement-mappings${queryString({ ...filter })}`,
    );
  }
  mapping(orgId: string, mappingId: string) {
    return this.http.get<{ mapping: RequirementMapping }>(orgId, `requirement-mappings/${mappingId}`);
  }
  createMapping(orgId: string, input: CreateMappingInput) {
    return this.http.post<{ mapping: RequirementMapping }>(orgId, "requirement-mappings", input);
  }
  /** Suggestions are always persisted as proposals — never as coverage. */
  suggestMappings(
    orgId: string,
    input: { requirementId: string; policyId?: string | null; minConfidence?: number; limit?: number },
  ) {
    return this.http.post<{ mappings: RequirementMapping[] }>(orgId, "requirement-mappings/suggestions", input);
  }
  decideMapping(orgId: string, mappingId: string, decision: MappingDecision, rationale?: string) {
    return this.http.post<{ mapping: RequirementMapping; verification: MappingVerification }>(
      orgId,
      `requirement-mappings/${mappingId}/decision`,
      { decision, ...(rationale ? { rationale } : {}) },
    );
  }
  mappingHistory(orgId: string, mappingId: string) {
    return this.http.get<{ history: MappingVerification[] }>(
      orgId,
      `requirement-mappings/${mappingId}/history`,
    );
  }
}
